import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/api";
import { useAuth } from "@/AuthContext";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, Megaphone, AlertTriangle, Wrench, Sparkles, Wallet, CheckCircle2, X } from "lucide-react";

const CATEGORY_META = {
  update:         { label: "Atualização",   color: "bg-blue-100 text-blue-800 border-blue-200",       icon: Sparkles },
  implementation: { label: "Implementação", color: "bg-violet-100 text-violet-800 border-violet-200", icon: Wrench },
  warning:        { label: "Aviso",         color: "bg-red-100 text-red-800 border-red-200",          icon: AlertTriangle },
  billing:        { label: "Cobrança",      color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Wallet },
  maintenance:    { label: "Manutenção",    color: "bg-amber-100 text-amber-800 border-amber-200",    icon: Wrench },
  tip:            { label: "Dica",          color: "bg-indigo-100 text-indigo-800 border-indigo-200", icon: Megaphone },
  welcome:        { label: "Bem-vindo",     color: "bg-pink-100 text-pink-800 border-pink-200",       icon: Megaphone },
};

function ytEmbedFromUrl(url) {
  if (!url) return null;
  try {
    const m1 = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
    if (m1) return `https://www.youtube.com/embed/${m1[1]}`;
    const m2 = url.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
    if (m2) return `https://www.youtube.com/embed/${m2[1]}`;
    const m3 = url.match(/youtube\.com\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{6,})/);
    if (m3) return `https://www.youtube.com/embed/${m3[1]}`;
  } catch (e) { /* noop */ }
  return null;
}

function playSound() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    const ctx = new AC();
    // alguns navegadores em mobile chegam em estado "suspended"
    if (ctx.state === "suspended" && ctx.resume) {
      try { ctx.resume(); } catch (e) {}
    }
    // Sequência de 2 bips (mais audível que um único tom)
    const makeBeep = (freq, startOffset, dur=0.18) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + startOffset);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + startOffset + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startOffset + dur);
      o.start(ctx.currentTime + startOffset);
      o.stop(ctx.currentTime + startOffset + dur + 0.05);
    };
    makeBeep(880, 0);
    makeBeep(1175, 0.22);
    return ctx.state === "running";
  } catch (e) { return false; }
}

export default function NotificationCenter() {
  const { user } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [justArrived, setJustArrived] = useState(false); // anima entrada
  const playedRef = useRef(new Set());

  const onLoginPaths = ["/login", "/reset-password"];
  const isPublic = onLoginPaths.includes(location.pathname)
    || location.pathname.startsWith("/catalogo/")
    || location.pathname.startsWith("/servico/")
    || location.pathname.startsWith("/pagamento")
    || location.pathname.startsWith("/extrato/")
    || location.pathname.startsWith("/pagar/")
    || location.pathname.startsWith("/landing");

  const load = useCallback(async () => {
    if (!user || user.role === "master" && !user.impersonating) {
      setItems([]); return;
    }
    try {
      const r = await api.get("/notifications/active", { _cache: false });
      const list = Array.isArray(r.data) ? r.data : [];
      // Detecta se há item novo (anima a entrada)
      const newOnes = list.filter(n => !playedRef.current.has("seen:" + n.id));
      setItems(list);
      if (list.length > 0) {
        setActiveIdx(0);
        if (newOnes.length > 0) {
          setJustArrived(true);
          setTimeout(() => setJustArrived(false), 1200);
        }
        // play sound only once per notif id
        list.forEach(n => {
          playedRef.current.add("seen:" + n.id);
          if (n.play_sound && !playedRef.current.has(n.id)) {
            playedRef.current.add(n.id);
            playSound(); // toca uma vez só; se for bloqueado pelo autoplay,
                         // toca quando o usuário clicar no chip pra abrir.
            api.post(`/notifications/${n.id}/read`).catch(()=>{});
          }
        });
      } else {
        setOpen(false);
      }
    } catch (e) { /* noop */ }
  }, [user]);

  useEffect(() => {
    if (!user || isPublic) return;
    if (user.role === "master" && !user.impersonating) return;
    load();
    const t = setInterval(load, 30000); // refresca a cada 30s
    // Quando o usuário volta pra aba, força recarregar (cobre o caso de PWA cache)
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") load();
    });
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, isPublic, load]);

  const current = items[activeIdx];

  const confirm = async () => {
    if (!current) return;
    try { await api.post(`/notifications/${current.id}/confirm`); } catch (e) {}
    const next = items.filter((_, i) => i !== activeIdx);
    if (next.length === 0) { setOpen(false); setItems([]); return; }
    setItems(next);
    setActiveIdx(0);
  };

  const skipForNow = () => {
    // Apenas fecha a UI; volta a aparecer no próximo carregamento
    setOpen(false);
  };

  if (isPublic) return null;
  if (!user) return null;
  if (user.role === "master" && !user.impersonating) return null;

  // Chip flutuante minimizado (símbolo + título). Clica pra expandir.
  if (!open && items.length > 0) {
    const first = items[0];
    const fmeta = CATEGORY_META[first.category] || CATEGORY_META.update;
    const FIcon = fmeta.icon;
    const handleOpen = () => {
      // Se o som ainda não tocou (autoplay bloqueado), aproveita o click do
      // usuário pra tocar agora — gesto explícito, autoriza WebAudio.
      if (first.play_sound && !playedRef.current.has("heard:" + first.id)) {
        playedRef.current.add("heard:" + first.id);
        playSound();
      }
      setActiveIdx(0);
      setOpen(true);
    };
    return (
      <button
        onClick={handleOpen}
        className={`notif-chip fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-40 max-w-[calc(100vw-2rem)] sm:max-w-sm bg-white dark:bg-[#0F1626] border border-amber-300 dark:border-amber-700 shadow-xl rounded-full pl-3 pr-4 py-2.5 flex items-center gap-2.5 hover:shadow-2xl active:scale-[0.98] transition-all ${justArrived ? "notif-chip-arrive" : ""}`}
        data-testid="notification-bell-button"
        title="Clique para ler"
      >
        <span className={`relative inline-flex h-7 w-7 rounded-full items-center justify-center ${fmeta.color}`}>
          {/* anel de pulse pra chamar atenção */}
          <span className="absolute inset-0 rounded-full notif-pulse-ring" aria-hidden="true"/>
          <FIcon size={13} className="relative z-10"/>
          {items.length > 1 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center z-20">
              {items.length}
            </span>
          )}
        </span>
        <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate text-left" data-testid="notification-chip-title">
          {first.title}
        </span>
        <Bell size={12} className="text-amber-500 flex-shrink-0 notif-bell-shake"/>
      </button>
    );
  }

  if (!current) return null;
  const meta = CATEGORY_META[current.category] || CATEGORY_META.update;
  const Icon = meta.icon;
  const yt = ytEmbedFromUrl(current.video_url);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skipForNow(); }}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-2xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        data-testid="notification-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{current.title}</DialogTitle>
        <DialogDescription className="sr-only">{current.subtitle || current.text?.slice(0, 80) || "Notificação"}</DialogDescription>
        <div className="relative">
          {items.length > 1 && (
            <div className="absolute top-3 left-4 z-10 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded bg-black/70 text-white">
              {activeIdx + 1} de {items.length}
            </div>
          )}
          <button onClick={confirm} className="absolute top-3 right-3 z-10 h-7 w-7 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center" title="Fechar e não mostrar mais" data-testid="notification-close-x">
            <X size={14}/>
          </button>
          {current.image_url && (
            <div className="w-full h-48 sm:h-56 bg-neutral-900 overflow-hidden">
              <img src={current.image_url} alt="" className="w-full h-full object-cover"/>
            </div>
          )}
          {yt && (
            <div className="w-full aspect-video bg-black">
              <iframe src={yt} title="video" allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full"/>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border ${meta.color}`}>
              <Icon size={11}/> {meta.label}
            </span>
            {current.subtitle && <span className="text-xs text-neutral-500">{current.subtitle}</span>}
          </div>
          <h2 className="font-display text-2xl tracking-tight" data-testid="notification-title">{current.title}</h2>
          <div className="mt-3 text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed" data-testid="notification-text">
            {current.text}
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={skipForNow} data-testid="notification-skip">Lembrar depois</Button>
            <Button onClick={confirm} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="notification-confirm">
              <CheckCircle2 size={14} className="mr-1.5"/> Ok, entendi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
